import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Home = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
      .then(res => res.json())
      .then(data => setProducts(data.slice(0, 3)))
      .catch(err => console.error('Failed to fetch products:', err));
  }, []);

  return (
    <div className="content-wrapper" data-testid="home-page">
      {/* Floating Fruit Decorations */}
      <motion.div 
        className="fruit-decoration top-20 left-10 w-24 h-24 opacity-30"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src="https://images.unsplash.com/photo-1592695953174-a6bc74c6a085" alt="" className="w-full h-full object-contain" />
      </motion.div>
      
      <motion.div 
        className="fruit-decoration top-40 right-20 w-32 h-32 opacity-30"
        animate={{ y: [0, -30, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        <img src="https://images.pexels.com/photos/36477948/pexels-photo-36477948.jpeg" alt="" className="w-full h-full object-contain" />
      </motion.div>

      {/* Hero Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black font-['Fredoka'] text-[#3A1C00] mb-6 tracking-tight" data-testid="hero-title">
              Juicy Lips,
              <br />
              <span className="brand-logo">Natural Care</span>
            </h1>
            <p className="text-xl sm:text-2xl text-[#7D5A35] font-['Nunito'] font-medium mb-12 max-w-2xl mx-auto leading-relaxed" data-testid="hero-subtitle">
              Discover our fruity lip care collection infused with natural ingredients for luscious, healthy lips.
            </p>
            <Link to="/products" data-testid="hero-cta-button">
              <button className="btn-primary text-lg px-10 py-5">
                Shop Now
              </button>
            </Link>
          </motion.div>

          {/* Hero Image */}
          <motion.div 
            className="mt-16 rounded-[3rem] overflow-hidden shadow-[0_20px_60px_rgba(255,184,0,0.2)] max-w-4xl mx-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            data-testid="hero-image-wrapper"
          >
            <img 
              src="https://images.unsplash.com/photo-1764403726655-5b24a34df0cc" 
              alt="Mango splash" 
              className="w-full h-[400px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#FFF1E5]" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-black font-['Fredoka'] text-[#3A1C00] text-center mb-16" data-testid="featured-products-title">
            Featured Products
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`featured-product-${index}`}
              >
                <Link to={`/products/${product.id}`}>
                  <div className="product-card group">
                    <div className="aspect-square rounded-3xl overflow-hidden mb-6 bg-[#FFFDF7]">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                        data-testid={`product-image-${index}`}
                      />
                    </div>
                    <div className="space-y-3">
                      <span className="badge badge-stock" data-testid={`product-stock-${index}`}>{product.stock_status}</span>
                      <h3 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00]" data-testid={`product-name-${index}`}>
                        {product.name}
                      </h3>
                      <p className="text-[#7D5A35] line-clamp-2 leading-relaxed" data-testid={`product-description-${index}`}>
                        {product.description}
                      </p>
                      <p className="text-3xl font-black font-['Fredoka'] text-[#FFB800]" data-testid={`product-price-${index}`}>
                        ${product.price.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/products" data-testid="view-all-products-button">
              <button className="btn-secondary text-lg px-10 py-5">
                View All Products
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="about-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-black font-['Fredoka'] text-[#3A1C00] mb-8" data-testid="about-title">
            Why Choose Mango Cosmetics?
          </h2>
          <p className="text-xl text-[#7D5A35] leading-relaxed mb-12" data-testid="about-description">
            We believe in the power of natural ingredients. Our lip care products are crafted with love, 
            infused with real fruit extracts, and designed to nourish your lips while making you smile.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-[2rem] p-8 shadow-sm" data-testid="benefit-natural">
              <div className="text-5xl mb-4">🥭</div>
              <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] mb-3">100% Natural</h3>
              <p className="text-[#7D5A35]">Made with real fruit extracts and natural ingredients</p>
            </div>
            
            <div className="bg-white rounded-[2rem] p-8 shadow-sm" data-testid="benefit-cruelty-free">
              <div className="text-5xl mb-4">🐰</div>
              <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] mb-3">Cruelty Free</h3>
              <p className="text-[#7D5A35]">Never tested on animals, always kind to nature</p>
            </div>
            
            <div className="bg-white rounded-[2rem] p-8 shadow-sm" data-testid="benefit-handmade">
              <div className="text-5xl mb-4">✨</div>
              <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] mb-3">Handmade</h3>
              <p className="text-[#7D5A35]">Crafted with care in small batches for quality</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;